import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'
import type { ProductType } from '@/shared/lib/product-type'

// ============================================================
// Типы (соответствуют backend/src/modules/order/order.types.ts)
// ============================================================

// Тип доставки (соответствует constraint в БД)
// - pickup    = Самовывоз
// - cdek_pvz  = СДЭК Пункт выдачи
// - cdek_door = СДЭК Курьер до двери
export type DeliveryType = 'pickup' | 'cdek_pvz' | 'cdek_door'
export type CdekPointType = 'PVZ' | 'POSTAMAT' | 'DOOR'
export type OrderStatus = 'pending_payment' | 'new' | 'confirmed' | 'in_progress' | 'ready' | 'completed' | 'cancelled'
export type PaymentStatus = 'pending' | 'succeeded' | 'canceled' | 'refunded'

/** Данные доставки СДЭК */
export interface CdekDeliveryInfo {
  pointCode: string
  pointName: string
  pointAddress: string
  pointCity: string
  pointWorkTime: string
  pointType: CdekPointType
  deliveryCost: number
  minDays: number
  maxDays: number
  tariffCode: number
  latitude: number
  longitude: number
  // Для курьерской доставки
  doorStreet?: string
  doorHouse?: string
  doorBuilding?: string
  doorApartment?: string
  doorEntrance?: string
  doorFloor?: string
  doorIntercom?: string
}

/** Позиция заказа */
export interface OrderItem {
  id: string
  orderId: string
  productType: ProductType
  productId: string
  productName: string
  productSku: string | null
  productImage: string | null
  unitPrice: number
  quantity: number
  weight: number | null
  subtotal: number
  sortOrder: number
  createdAt: string
}

/** Краткая информация о заказе (для списка) */
export interface OrderSummary {
  id: string
  userId: number
  itemsAmount: number
  shippingCost: number
  giftCardAmount: number
  totalAmount: number
  currency: string
  status: OrderStatus
  paymentStatus: PaymentStatus | null
  deliveryType: DeliveryType
  shippingAddress: string | null
  recipientName: string | null
  itemsCount: number
  createdAt: string
  updatedAt: string
}

/** Полная информация о заказе (с items) */
export interface Order extends OrderSummary {
  shippingComment: string | null
  giftCardId: string | null
  telegramMessageId: string | null
  cancelledAt: string | null
  cancelReason: string | null
  // Оплата (ЮKасса)
  paymentId: string | null
  paymentMethod: string | null
  paidAt: string | null
  paymentUrl: string | null
  paymentExpiresAt: string | null
  refundId: string | null
  refundedAt: string | null
  refundedAmount: number | null
  items: OrderItem[]
  user?: {
    id: number
    firstName?: string
    lastName?: string
    username?: string
    phone?: string
  }
  // Данные СДЭК
  cdekPointCode: string | null
  cdekPointName: string | null
  cdekPointAddress: string | null
  cdekPointCity: string | null
  cdekPointWorkTime: string | null
  cdekPointType: CdekPointType | null
  cdekDeliveryCost: number | null
  cdekDeliveryMinDays: number | null
  cdekDeliveryMaxDays: number | null
  cdekTariffCode: number | null
  cdekPointLatitude: number | null
  cdekPointLongitude: number | null
  // Адрес курьерской доставки
  cdekDoorStreet: string | null
  cdekDoorHouse: string | null
  cdekDoorBuilding: string | null
  cdekDoorApartment: string | null
  cdekDoorEntrance: string | null
  cdekDoorFloor: string | null
  cdekDoorIntercom: string | null
  // СДЭК заказ
  cdekOrderUuid: string | null
  cdekOrderNumber: string | null
  // ФИО получателя
  recipientName: string | null
}

/** Запрос на создание заказа */
export interface CreateOrderRequest {
  deliveryType: DeliveryType
  shippingAddress?: string
  shippingComment?: string
  giftCardCode?: string
  phone?: string
  /** Email для фискального чека ЮKасса */
  email?: string
  /** ФИО получателя (обязательно для СДЭК) */
  recipientName?: string
  /** Способ оплаты: 'online' (ЮKасса) или 'cash' (при получении, только для самовывоза) */
  paymentType?: 'online' | 'cash'
  /** Данные СДЭК (обязательны при deliveryType === 'cdek_pvz' или 'cdek_door') */
  cdek?: CdekDeliveryInfo
}

// ============================================================
// API Response Types
// ============================================================

interface OrdersListResponse {
  success: boolean
  data: OrderSummary[]
}

interface OrderDetailResponse {
  success: boolean
  data: Order
}

interface PaymentStatusResponse {
  success: boolean
  data: {
    orderId: string
    status: OrderStatus
    paymentStatus: PaymentStatus | null
  }
}

// ============================================================
// Hooks
// ============================================================

/**
 * Получить список заказов пользователя
 * GET /api/user/orders → OrderSummary[] (без items)
 */
export function useOrders() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      if (!initData) throw new Error('Требуется авторизация')
      const response = await apiClient<OrdersListResponse>('/api/user/orders', { initData })
      return response.data || []
    },
    enabled: !!initData,
  })
}

/**
 * Получить детали заказа по ID
 * GET /api/user/orders/:id → Order (с items)
 */
export function useOrderById(orderId: string | undefined) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['orders', orderId],
    queryFn: async () => {
      if (!initData) throw new Error('Требуется авторизация')
      if (!orderId) throw new Error('orderId required')
      const response = await apiClient<OrderDetailResponse>(`/api/user/orders/${orderId}`, { initData })
      return response.data
    },
    enabled: !!initData && !!orderId,
  })
}

/**
 * Поллинг статуса оплаты (для страницы payment-result)
 * GET /api/user/orders/:id/payment-status
 */
export function usePaymentStatus(orderId: string | undefined, enabled = true) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['payment-status', orderId],
    queryFn: async () => {
      if (!initData || !orderId) throw new Error('Not ready')
      const response = await apiClient<PaymentStatusResponse>(
        `/api/user/orders/${orderId}/payment-status`,
        { initData }
      )
      return response.data
    },
    enabled: !!initData && !!orderId && enabled,
    refetchInterval: 3000,
  })
}

/**
 * Создать заказ из корзины
 * POST /api/user/orders
 */
export function useCreateOrder() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateOrderRequest) => {
      if (!initData) throw new Error('Требуется авторизация')
      const response = await apiClient<OrderDetailResponse>('/api/user/orders', {
        method: 'POST',
        body: JSON.stringify(data),
        initData,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['cart'] })
    },
  })
}

/**
 * Привязать гостевой заказ (с сайта) к текущему аккаунту Telegram.
 * POST /api/user/orders/claim
 */
export function useClaimOrder() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderId: string) => {
      if (!initData) throw new Error('Требуется авторизация')
      const response = await apiClient<OrderDetailResponse>('/api/user/orders/claim', {
        method: 'POST',
        body: JSON.stringify({ orderId }),
        initData,
      })
      return response.data
    },
    onSuccess: (_, orderId) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['orders', orderId] })
      queryClient.invalidateQueries({ queryKey: ['public-order', orderId] })
    },
  })
}

/**
 * Получить публичные данные заказа (для непривязанных заказов).
 * GET /api/public/orders/:id
 * Используется как fallback когда useOrderById возвращает 404.
 */
export function usePublicOrder(orderId: string | undefined, enabled = false) {
  return useQuery({
    queryKey: ['public-order', orderId],
    queryFn: async () => {
      if (!orderId) throw new Error('orderId required')
      const response = await apiClient<OrderDetailResponse>(`/api/public/orders/${orderId}`)
      return response.data
    },
    enabled: !!orderId && enabled,
  })
}

/**
 * Отменить заказ (только pending_payment)
 * POST /api/user/orders/:id/cancel
 */
export function useCancelOrder() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      if (!initData) throw new Error('Требуется авторизация')
      const response = await apiClient<OrderDetailResponse>(`/api/user/orders/${orderId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
        initData,
      })
      return response.data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['orders', variables.orderId] })
    },
  })
}
